import React from 'react';

import cs from 'classnames';
import filter from 'lodash/filter';

import {DocumentActionsButton} from 'app/components/general/DocumentActionsButton';
import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {ItemRelationsConnection} from 'app/folders/components/ItemRelationsConnection';
import {
  StyledCompactItemHeaderDiv,
  StyledCompactItemHeaderSpan,
} from 'app/general/bootstrap/utils';
import {DocIsDraftBadge} from 'app/general/dashboards/DocIsDraftBadge';
import {ItemLatestActivity} from 'app/general/dashboards/ItemLastActivity';
import {ItemRelationsBadges} from 'app/general/dashboards/ItemRelationsBadges';
import {ItemRelationsListOfGenerics} from 'app/general/dashboards/ItemRelationsListOfGenerics';
import {ItemRelationsListOfTeams} from 'app/general/dashboards/ItemRelationsListOfTeams';
import {ItemRelationsLists} from 'app/general/dashboards/ItemRelationsLists';
import {DocumentSequencedStepsStatusIcon} from 'app/general/doc-status';
import {DocumentIconWithNotifications} from 'app/notifly/components/DocumentIconWithNotifications';
import {DashboardItemPresentMembers} from 'app/presence/components/DashboardItemPresentMembers';
import {DocNeedsReviewBadge} from 'app/review/components/DocNeedReviewBadge';
import {getObjectName} from 'app/utils/fallbacks';

import type {ConnectionType} from 'app/documents/types';
import type {BadgeRelation} from 'app/general/dashboards/ItemRelationsBadges';
import type {NotificationType} from 'app/notifly/types';
import type {SortOptionType} from 'app/utils/constants';
import type {ContentType, ProcessSerializedType} from 'sweet-types';

type CompactProps = {
  object: ProcessSerializedType;
  sortBy?: SortOptionType;
  refreshData: () => Promise<unknown>;
  activeRelations?: Record<ContentType, boolean>;
  toggleRelation?: (type: ContentType, activeState: boolean) => void;
  notifications?: Array<NotificationType>;
};

const processRelations: Array<BadgeRelation> = [
  {
    ct: 'tag',
    getter: (p: ProcessSerializedType) => p.tags,
    Component: ItemRelationsListOfGenerics,
  },
  {
    ct: 'team',
    getter: (p: ProcessSerializedType) =>
      Array.from(new Set(p.team_memberships.map((team) => team.team))),
    Component: ItemRelationsListOfTeams,
  },
  {
    ct: 'procedure',
    getter: (p: ProcessSerializedType) =>
      p.content.filter((content) => content.type === 'processprocedure'),
    Component: ItemRelationsListOfGenerics,
  },
  {
    ct: 'decision',
    getter: (p: ProcessSerializedType) =>
      p.content
        .filter((content) => content.type === 'decision')
        .map((content) => ({
          ...content,
          name: content.title,
        })),
    Component: ItemRelationsListOfGenerics,
  },
  {
    ct: 'policy',
    getter: (p: ProcessSerializedType) => p.policies,
    Component: ItemRelationsListOfGenerics,
  },
  {
    ct: 'folder',
    getter: (p: ProcessSerializedType) => {
      const connections = p.connections;
      return filter(connections, (connection: ConnectionType) => {
        return (
          connection.team == null &&
          connection.process == null &&
          connection.folder != null
        );
      });
    },
    Component: ItemRelationsConnection,
  },
];

export const CompactProcessListItem = ({
  object,
  activeRelations,
  toggleRelation,
  sortBy,
  notifications,
  refreshData,
}: CompactProps) => {
  return (
    <div
      className={cs(
        'row border-bottom py-2 d-flex align-items-center position-relative',
        {'opacity-overlay': object.approved_at == null}
      )}
    >
      <DashboardItemPresentMembers
        contentType="proceduregroup"
        objectId={object.id}
      />
      <StyledCompactItemHeaderDiv>
        <FullHTMLLink
          html_url={object.html_url}
          className="text-dark font-weight-bold d-block d-sm-inline"
        >
          <StyledCompactItemHeaderSpan>
            <DocumentIconWithNotifications
              contentType="proceduregroup"
              notifications={notifications}
            />{' '}
            {getObjectName(object, 'process', true)}
            <DocIsDraftBadge approved_at={object.approved_at} />
            {object.next_review_at != null ? (
              <DocNeedsReviewBadge reviewDate={object.next_review_at} />
            ) : null}
          </StyledCompactItemHeaderSpan>
        </FullHTMLLink>
      </StyledCompactItemHeaderDiv>

      {toggleRelation != null && activeRelations != null ? (
        <ItemRelationsBadges
          className="col-auto ml-auto ml-sm-0 order-lg-3"
          relations={processRelations}
          object={object}
          activeRelations={activeRelations}
          onToggle={toggleRelation}
        />
      ) : null}

      <div className="col-10 col-sm-6 col-lg-3 mr-auto order-lg-4">
        <ItemLatestActivity sortBy={sortBy} object={object} />
      </div>

      {object.steps_blocked_at != null ? (
        <div className="pl-0 col-2 col-sm-auto text-right col-lg-auto order-lg-2">
          <DocumentSequencedStepsStatusIcon
            isSequenced={object.steps_blocked_at != null}
            contentType="proceduregroup"
          />
        </div>
      ) : null}

      <div className="col-auto order-lg-5">
        <DocumentActionsButton
          document={object}
          pageContext="dashboard"
          refreshData={refreshData}
        />
      </div>

      <div className="col-12 order-last">
        <ItemRelationsLists
          object={object}
          activeRelations={activeRelations}
          relations={processRelations}
        />
      </div>
    </div>
  );
};
